import React, { useEffect, useState } from "react";
import { Download, BellRing, X } from "lucide-react";
import { isWebPushSupported, ensureWebPushSubscribed } from "@/lib/webpush";
import { toast } from "sonner";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

/** PWA banners: (1) install to home screen, (2) enable trip push alerts.
 *  Dismissals persist in localStorage so we never nag. Copy is per-audience
 *  (driver default; Customer portal passes passenger copy + its own storage keys). */
export const DriverAppBanners = ({
  installTitle = "Install the driver app",
  installBody = "One tap adds Delux Limo to your home screen — full screen, faster, with trip alerts.",
  pushTitle = "Get trip alerts",
  pushBody = "Know the second dispatch assigns you a trip — even when the app is closed.",
  pushSuccess = "Trip alerts on — you'll get a notification when dispatch assigns you",
  storagePrefix = "drv",
} = {}) => {
  const [installEvt, setInstallEvt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showPush, setShowPush] = useState(false);

  useEffect(() => {
    const dismissedInstall = localStorage.getItem(`${storagePrefix}InstallDismissed`) === "1";
    const dismissedPush = localStorage.getItem(`${storagePrefix}PushDismissed`) === "1";
    if (!isStandalone() && !dismissedInstall) {
      if (isIOS()) setShowInstall(true); // iOS: manual instructions
      const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); setShowInstall(true); };
      window.addEventListener("beforeinstallprompt", onPrompt);
      return () => window.removeEventListener("beforeinstallprompt", onPrompt);
    }
    if (isWebPushSupported() && Notification.permission !== "granted" && !dismissedPush) {
      setShowPush(true);
    }
  }, []);

  // Once installed (standalone) push banner becomes the priority
  useEffect(() => {
    if (isStandalone() && isWebPushSupported() && Notification.permission === "granted") {
      ensureWebPushSubscribed(); // silent re-sync of the subscription
    }
  }, []);

  const install = async () => {
    if (installEvt) {
      installEvt.prompt();
      const { outcome } = await installEvt.userChoice;
      if (outcome === "accepted") { setShowInstall(false); toast.success("App installed — find it on your home screen"); }
    }
  };

  const enablePush = async () => {
    const r = await ensureWebPushSubscribed();
    if (r.ok) { toast.success(pushSuccess); setShowPush(false); }
    else if (r.reason === "denied") toast.error("Notifications are blocked — enable them in your phone settings");
    else toast.error("Couldn't enable notifications on this device");
  };

  if (showInstall) {
    return (
      <div className="mx-5 mb-4 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 flex items-start gap-3" data-testid="install-app-banner">
        <Download size={18} className="text-[#D4AF37] mt-0.5 shrink-0"/>
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#F5F2EB]">{installTitle}</div>
          <p className="text-xs text-[#A1A1AA] mt-1">
            {isIOS() && !installEvt
              ? "Tap the Share button in Safari, then \u201CAdd to Home Screen\u201D — you'll get a real app icon."
              : installBody}
          </p>
          {installEvt && (
            <button data-testid="install-app-btn" onClick={install} className="btn-gold !py-2 !px-4 text-[10px] mt-3">Install app</button>
          )}
        </div>
        <button data-testid="install-dismiss" onClick={() => { setShowInstall(false); localStorage.setItem(`${storagePrefix}InstallDismissed`, "1"); }} className="text-[#71717a] hover:text-white"><X size={14}/></button>
      </div>
    );
  }

  if (showPush) {
    return (
      <div className="mx-5 mb-4 border border-white/15 bg-[#0A0A0A] p-4 flex items-start gap-3" data-testid="push-banner">
        <BellRing size={18} className="text-[#D4AF37] mt-0.5 shrink-0"/>
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#F5F2EB]">{pushTitle}</div>
          <p className="text-xs text-[#A1A1AA] mt-1">{pushBody}</p>
          <button data-testid="enable-push-btn" onClick={enablePush} className="btn-gold !py-2 !px-4 text-[10px] mt-3">Turn on notifications</button>
        </div>
        <button data-testid="push-dismiss" onClick={() => { setShowPush(false); localStorage.setItem(`${storagePrefix}PushDismissed`, "1"); }} className="text-[#71717a] hover:text-white"><X size={14}/></button>
      </div>
    );
  }
  return null;
};
